import type { ReactNode } from 'react'
import { SITE_HOST, SITE_URL } from '@/lib/config/site'
import {
  Rocket,
  Bot,
  Users,
  Film,
  Sparkles,
  Share2,
  Mic,
  Captions,
  Music,
  Palette,
  Terminal,
  CreditCard,
  Layers,
  Wand2,
  ScanFace,
  Type,
  Settings,
  MonitorPlay,
  type LucideIcon,
} from 'lucide-react'

export interface DocsCard {
  icon: LucideIcon
  title: string
  description: string
  href: string
}

export interface DocsStep {
  title: string
  content: ReactNode
}

export interface DocsSection {
  id: string
  heading: string
  content: ReactNode
  bullets?: string[]
  tip?: string
  warning?: string
  note?: string
  cards?: DocsCard[]
  steps?: DocsStep[]
  image?: {
    src: string
    alt: string
    caption?: string
  }
}

export interface DocsArticle {
  slug: string
  title: string
  description: string
  icon: LucideIcon
  sections: DocsSection[]
}

export interface DocsCategory {
  label: string
  articles: DocsArticle[]
}

export const categories: DocsCategory[] = [
  {
    label: 'Getting Started',
    articles: [
      {
        slug: 'quick-start',
        title: 'Quick Start',
        description: 'Create your first AI-generated video in under 5 minutes. Step-by-step walkthrough from sign-up to export.',
        icon: Sparkles,
        sections: [
          {
            id: 'five-steps',
            heading: 'From Idea to Video in 5 Steps',
            content: (
              <>
                No video editing experience needed. Describe what you want, and the AI handles scene planning, generation, and assembly.
              </>
            ),
            steps: [
              { title: 'Create your account', content: <>Sign up with your <strong>Google account</strong> — no separate password needed. Click "Get started" on the homepage. You'll land in the studio immediately with free credits to start creating.</> },
              { title: 'Create a video', content: <>Click <strong>"New video"</strong> in the studio. You'll enter the video editor — a split-screen with the AI chat on the left and your video timeline on the right.</> },
              { title: 'Describe your vision', content: <>Tell the AI what you want to create. Be specific about the mood, setting, and visual style you're going for. Footage breaks your idea into scenes and creates clips with optimized prompts for each one.</> },
              { title: 'Review and generate', content: <>Clips appear on your timeline as cards with text prompts. Review and tweak any prompt, then click <strong>"Generate All"</strong> to start video generation. Each clip takes 30-90 seconds depending on the model.</> },
              { title: 'Refine and export', content: <>Preview your clips, regenerate any that need work, and add voiceovers, music, captions, and effects. When you're happy, export the final video — you get a downloadable file and a shareable link.</> },
            ],
            tip: 'Example: "Close-up shot of a woman\'s hands typing on a laptop in a minimalist home office. Soft golden hour light streams through floor-to-ceiling windows. Camera slowly pulls back to reveal the full desk setup. Calm, focused atmosphere."',
            image: {
              src: '/docs/studio-dashboard.png',
              alt: 'Footage studio showing finished videos with thumbnails, ready to share or continue editing',
              caption: 'Your studio with completed videos ready to share, embed, or continue editing',
            },
          },
          {
            id: 'next-steps',
            heading: 'Next Steps',
            content: (
              <>
                Now that you've created your first video, explore these areas to get the most out of Footage.
              </>
            ),
            cards: [
              { icon: Bot, title: 'Prompting Guide', description: 'Tips for getting the best results from the AI', href: '/docs/prompting-guide' },
              { icon: Users, title: 'Characters', description: 'Create consistent characters across clips', href: '/docs/characters' },
              { icon: Sparkles, title: 'AI Models', description: 'Choose the right model for your content', href: '/docs/ai-models' },
              { icon: Music, title: 'Music & Audio', description: 'Add voiceovers, music, and sound', href: '/docs/music-library' },
            ],
          },
        ],
      },
      {
        slug: 'introduction',
        title: 'What is Footage?',
        description: 'A comprehensive overview of Footage — the AI-powered video creation studio that turns ideas into polished video projects.',
        icon: Rocket,
        sections: [
          {
            id: 'overview',
            heading: 'Overview',
            content: (
              <>
                Footage is an AI-powered video creation studio. You describe what you want — a product demo, an explainer, a social ad, a short film — and Footage's AI creative director plans the narrative, generates video clips, adds voiceovers, applies effects, and assembles everything on a timeline. The result is a complete, exportable video.
              </>
            ),
          },
          {
            id: 'how-it-works',
            heading: 'How It Works',
            content: (
              <>
                The core workflow follows four steps.
              </>
            ),
            steps: [
              { title: 'Describe', content: 'Tell the AI what you want to create using natural language. Describe your concept, mood, target audience, and any specific visual elements.' },
              { title: 'Generate', content: 'AI produces video clips from your descriptions using multiple video generation models. Each clip gets an optimized prompt for the best results.' },
              { title: 'Edit', content: 'Arrange clips on a timeline, add voiceovers, music, effects, and captions. Refine prompts and regenerate any clip that doesn\'t match your vision.' },
              { title: 'Share', content: 'Export your finished video or share it with a public link. Embed on any website with the lightweight player.' },
            ],
          },
          {
            id: 'who-is-it-for',
            heading: 'Who Is It For?',
            content: (
              <>
                Footage is built for anyone who needs video content but doesn't want to manage a traditional production pipeline. That includes solo creators, marketing teams, product managers building demos, educators creating course content, and agencies producing video at scale.
              </>
            ),
            bullets: [
              'Content creators — YouTube, TikTok, Instagram Reels, and social media content',
              'Marketing teams — product demos, explainer videos, social ads, and brand stories',
              'Educators — course content, tutorials, and how-to videos',
              'Agencies — client video production at scale without a full production crew',
              'Product teams — feature walkthroughs, announcement videos, and internal comms',
            ],
          },
          {
            id: 'key-capabilities',
            heading: 'Key Capabilities',
            content: (
              <>
                Footage combines several AI technologies into a single workflow. Here's what's under the hood.
              </>
            ),
            bullets: [
              'AI Creative Director — Plans narratives, writes prompts, and manages your timeline',
              'Multi-model video generation — Multiple AI video models optimized for different styles and use cases',
              'Text-to-speech — AI voiceovers with 20+ voices, tone modifiers, and per-character voice assignment',
              'Lip sync — Post-processing that matches character mouth movements to voiceover audio',
              'Music library — 282 royalty-free tracks across 14 categories',
              'Visual effects — Color grading, motion presets, keyframe animation, and 12+ effects',
              'Captions — Auto-transcription with word-level timing and customizable styles',
              'Text overlays — 28+ typography presets with animation',
              'Character system — Consistent character appearances across clips using reference images',
              'Share and embed — Public share links and embeddable video player',
            ],
          },
          {
            id: 'explore',
            heading: 'Explore the Docs',
            content: (
              <>
                Dive deeper into each feature area.
              </>
            ),
            cards: [
              { icon: Sparkles, title: 'Quick Start', description: 'Create your first video in 5 minutes', href: '/docs/quick-start' },
              { icon: Bot, title: 'AI Creative Director', description: 'How Footage plans and creates your video', href: '/docs/ai-agent' },
              { icon: Film, title: 'Timeline & Editing', description: 'Clips, transitions, motion, and text overlays', href: '/docs/timeline' },
              { icon: Mic, title: 'Audio & Music', description: 'Voiceovers, TTS, music library, and lip sync', href: '/docs/music-library' },
            ],
          },
        ],
      },
      {
        slug: 'studio-overview',
        title: 'Studio Overview',
        description: 'Navigate the Footage studio — your dashboard for managing projects, assets, templates, and account settings.',
        icon: MonitorPlay,
        sections: [
          {
            id: 'studio-dashboard',
            heading: 'The Studio Dashboard',
            content: (
              <>
                The studio is your home base. It displays all your projects in a grid layout with thumbnails, titles, and status indicators. Projects are sorted by most recent activity. Use the search bar to find specific projects by name.
              </>
            ),
            image: {
              src: '/docs/studio-dashboard.png',
              alt: 'Footage studio dashboard showing project grid with thumbnails, search bar, and sidebar navigation',
              caption: 'The studio dashboard with project cards showing thumbnails, status badges, and quick actions',
            },
          },
          {
            id: 'sidebar-navigation',
            heading: 'Sidebar Navigation',
            content: (
              <>
                The left sidebar provides access to all areas of the app.
              </>
            ),
            bullets: [
              'Studio — Your project dashboard',
              'Assets — Generated images, videos, and audio files',
              'Templates — Pre-built project templates for common use cases',
              'Uploads — Files you\'ve uploaded (images, reference photos)',
              'Avatars — Character avatar library',
              'Voices — Voice library for text-to-speech',
              'Trash — Recently deleted projects (30-day retention)',
              'Settings — Account, subscription, and app preferences',
            ],
          },
          {
            id: 'project-states',
            heading: 'Project States',
            content: (
              <>
                Each project moves through a lifecycle of states as you work on it.
              </>
            ),
            bullets: [
              'Script — Initial state, building the narrative and scene plan',
              'Scenes — Scenes and clips have been planned, ready for generation',
              'Generating — Video clips are being generated by AI models',
              'Preview — Clips are generated and ready for review',
              'Completed — Project is finished and exported',
            ],
          },
        ],
      },
    ],
  },
  {
    label: 'AI Creative Director',
    articles: [
      {
        slug: 'ai-agent',
        title: 'How the AI Director Works',
        description: 'Understanding the AI creative director that plans narratives, writes prompts, creates clips, and manages your entire video project.',
        icon: Bot,
        sections: [
          {
            id: 'what-is-the-agent',
            heading: 'What Is the AI Director?',
            content: (
              <>
                The AI creative director lives in the chat panel on the left side of the project editor. Think of it as a skilled video producer who understands cinematography, narrative structure, and AI video generation — and can execute on your ideas immediately.
              </>
            ),
            image: {
              src: '/docs/project-editor.png',
              alt: 'Footage project editor showing AI chat panel on the left, video preview in the center, and clip timeline at the bottom',
              caption: 'The project editor: AI chat on the left, video preview center, timeline and clips below',
            },
          },
          {
            id: 'agent-capabilities',
            heading: 'What It Can Do',
            content: (
              <>
                Footage handles the full spectrum of creative direction and project management.
              </>
            ),
            bullets: [
              'Plan multi-scene narratives from a single concept description',
              'Write optimized video generation prompts with cinematic detail (camera angles, lighting, movement)',
              'Create, edit, reorder, and remove clips on your timeline',
              'Define characters with physical descriptions, voices, and reference images',
              'Write dialog lines assigned to specific characters with tone modifiers',
              'Set project-level settings like aspect ratio, default model, and narrative style',
              'Crawl URLs you share (product pages, articles, references) to understand context',
              'Suggest creative directions, visual styles, and narrative improvements',
              'Auto-continue workflows — when clips are generated, Footage can automatically plan next steps',
            ],
          },
          {
            id: 'agent-workflow',
            heading: 'How It Works',
            content: (
              <>
                When you describe a concept, Footage follows a structured creative process. It analyzes your intent, plans a scene-by-scene breakdown, writes detailed video prompts for each clip, and creates them on your timeline. Footage considers shot composition, visual continuity between clips, pacing, and narrative arc.
              </>
            ),
            tip: 'Footage writes prompts designed specifically for AI video models. These prompts include details about camera movement, lighting, subject action, and environment that you might not think to specify — resulting in higher-quality generated clips.',
          },
          {
            id: 'auto-continue',
            heading: 'Auto-Continue',
            content: (
              <>
                Footage supports automatic continuation. When it creates clips and they start generating, it can continue working on the next phase of the project without waiting for you to prompt it again. This is useful for complex projects where Footage needs multiple turns to complete the full narrative.
              </>
            ),
            note: 'Auto-continue is enabled by default in Director mode. Footage will plan the next batch of clips while the current ones are generating.',
          },
        ],
      },
      {
        slug: 'prompting-guide',
        title: 'Prompting Guide',
        description: 'Tips and techniques for getting the best results from Footage and video generation models.',
        icon: Wand2,
        sections: [
          {
            id: 'talking-to-the-agent',
            heading: 'Talking to Footage',
            content: (
              <>
                Footage understands natural language. You don't need special syntax or commands — just describe what you want in plain English. The more context you provide, the better the output.
              </>
            ),
          },
          {
            id: 'good-prompts',
            heading: 'What Makes a Good Prompt',
            content: (
              <>
                Whether you're writing prompts for Footage or for individual clips, specificity drives quality. Here's what to include.
              </>
            ),
            bullets: [
              'Subject — Who or what is in the scene? Be specific about appearance, clothing, and posture.',
              'Action — What is happening? Describe motion, gestures, and expressions.',
              'Environment — Where does the scene take place? Include details about lighting, time of day, and setting.',
              'Camera — What type of shot? Close-up, wide angle, tracking, static, drone, handheld?',
              'Mood — What feeling should the scene evoke? Cinematic, energetic, calm, mysterious, warm?',
              'Style — Photorealistic, animated, vintage film look, neon-lit, documentary, etc.',
            ],
            tip: 'Example: "Close-up shot of a woman\'s hands typing on a laptop in a minimalist home office. Soft golden hour light streams through floor-to-ceiling windows. Camera slowly pulls back to reveal the full desk setup. Calm, focused atmosphere."',
          },
          {
            id: 'high-level-vs-detailed',
            heading: 'High-Level vs. Detailed Descriptions',
            content: (
              <>
                You can work at different levels of abstraction. Give Footage a high-level concept like <em>"Make a 60-second product demo for a meditation app"</em> and it will plan everything. Or go granular: <em>"Add a clip showing a time-lapse of a city skyline transitioning from day to night, 4K cinematic, slow zoom out."</em> Both approaches work — use whatever matches your creative process.
              </>
            ),
          },
          {
            id: 'iterating',
            heading: 'Iterating on Results',
            content: (
              <>
                AI video generation is iterative. Your first generation might not be perfect — and that's expected. Here's how to iterate effectively.
              </>
            ),
            bullets: [
              'Regenerate — Same prompt, different output. Each generation is unique, so try again if the first take isn\'t right.',
              'Refine the prompt — Add more detail about what you wanted to see differently. Mention specific issues ("make the lighting warmer", "slower camera movement").',
              'Ask Footage — Tell it what you liked and didn\'t like. It will adjust the prompts accordingly.',
              'Try a different model — Each model has different strengths. Some excel at motion and natural scenes, others at character consistency or stylized content.',
            ],
          },
          {
            id: 'url-context',
            heading: 'Using URLs for Context',
            content: (
              <>
                Paste any URL into the chat and Footage will crawl and analyze it. This is especially powerful for product demos — share your product's landing page and Footage will understand the product, brand voice, and target audience, then create a video that matches.
              </>
            ),
            tip: 'Works with any public web page — product pages, blog posts, competitor videos, design references, mood boards.',
          },
        ],
      },
      {
        slug: 'director-vs-manual',
        title: 'Director vs. Manual Mode',
        description: 'Understanding the two project modes: let the AI drive, or take full creative control.',
        icon: Settings,
        sections: [
          {
            id: 'director-mode',
            heading: 'Director Mode',
            content: (
              <>
                In Director mode, Footage takes the lead. Describe your concept and Footage plans the entire video — narrative arc, scene breakdown, clip prompts, character definitions, and dialog. It manages the timeline end-to-end.
              </>
            ),
            bullets: [
              'Best for: Getting from idea to video as fast as possible',
              'Footage plans narrative structure and pacing',
              'Clips are created with optimized prompts automatically',
              'You review and refine the work',
              'Great for people who want to focus on the big picture',
            ],
          },
          {
            id: 'manual-mode',
            heading: 'Manual Mode',
            content: (
              <>
                In Manual mode, you have full control over every aspect of the project. Add clips yourself, write your own prompts, choose models per clip, and manage the timeline directly. Footage is still available in the chat for help, but it doesn't take autonomous action.
              </>
            ),
            bullets: [
              'Best for: Precise creative control over every detail',
              'You add clips and write prompts manually',
              'Full control over model selection, duration, and settings per clip',
              'Footage available as an advisor but doesn\'t modify the project automatically',
              'Ideal for experienced users or specific creative visions',
            ],
          },
          {
            id: 'switching-modes',
            heading: 'Switching Between Modes',
            content: (
              <>
                You can switch between Director and Manual mode at any point in your project. Start with Director mode to get a foundation, then switch to Manual mode to fine-tune specific clips. Or start manually and hand off to Footage when you want it to fill in gaps.
              </>
            ),
          },
        ],
      },
    ],
  },
  {
    label: 'Characters & Voices',
    articles: [
      {
        slug: 'characters',
        title: 'Creating Characters',
        description: 'Define persistent characters with physical descriptions, reference images, and voices for visual consistency across your video.',
        icon: Users,
        sections: [
          {
            id: 'why-characters',
            heading: 'Why Characters Matter',
            content: (
              <>
                Without characters, AI video models generate different-looking people in every clip. Characters solve this. When you define a character, Footage injects their physical description and reference image into every clip they appear in, giving the AI model a consistent visual target.
              </>
            ),
          },
          {
            id: 'creating-a-character',
            heading: 'Creating a Character',
            content: (
              <>
                Characters can be created by Footage or manually. To create one manually, open the Characters panel in the project editor. Each character has a name, a detailed physical description, and optionally a reference image and voice.
              </>
            ),
            bullets: [
              'Name — A short identifier used in dialog and scene descriptions',
              'Description — Detailed physical appearance: face shape, hair color/style, skin tone, build, clothing, distinguishing features',
              'Reference Image — A clear photo or illustration that AI models use for visual matching',
              'Voice — The TTS voice used when this character speaks',
            ],
            tip: 'Write character descriptions as if you\'re briefing a costume and makeup department. Include specific, observable details rather than personality traits. "Tall woman with auburn hair in a loose braid, olive skin, wearing a charcoal wool coat" is better than "confident woman in her 30s."',
            image: {
              src: '/docs/characters.png',
              alt: 'Character panel showing a fox character named Roger with reference images, physical description, and voice assignment',
              caption: 'Character panel with AI-generated reference images, detailed description, and voice settings',
            },
          },
          {
            id: 'reference-images',
            heading: 'Reference Images',
            content: (
              <>
                Reference images dramatically improve character consistency. Upload a clear, well-lit photo of the character (real person, illustration, or AI-generated portrait). The AI video model uses this image to match facial features, hair, clothing, and other visual details across all clips featuring that character.
              </>
            ),
            bullets: [
              'Use front-facing, well-lit photos for best results',
              'Clear facial features matter more than resolution',
              'The reference image is sent to the AI model alongside each clip prompt',
              'Supported by models with reference-to-video capability (see AI Models page for details)',
            ],
            warning: 'Reference images work best with models that support reference-to-video generation. When using text-to-video models, the character description is appended to the prompt instead.',
          },
        ],
      },
      {
        slug: 'voices',
        title: 'Voice Library & TTS',
        description: 'Browse AI voices, assign them to characters, and generate natural-sounding voiceovers with text-to-speech.',
        icon: Mic,
        sections: [
          {
            id: 'voice-library',
            heading: 'The Voice Library',
            content: (
              <>
                Footage includes a curated library of 20+ AI voices. Each voice has a distinct character — from warm and conversational to authoritative and professional. Browse voices from the <strong>Voices</strong> page in the sidebar, or use the voice picker when assigning voices to characters.
              </>
            ),
            note: 'Footage uses state-of-the-art text-to-speech models that deliver natural-sounding speech with appropriate intonation and pacing across multiple languages.',
          },
          {
            id: 'previewing-voices',
            heading: 'Previewing Voices',
            content: (
              <>
                Every voice has a sample audio clip you can preview before selecting it. Listen to the tone, pace, and character of each voice to find the right match for your project. Voice descriptions include details about accent, energy level, and ideal use cases.
              </>
            ),
          },
          {
            id: 'text-to-speech',
            heading: 'Text-to-Speech Generation',
            content: (
              <>
                Write dialog text on any clip, and Footage generates a voiceover using AI text-to-speech. The TTS engine produces natural-sounding speech with appropriate intonation and pacing. You can generate TTS per-clip or for the entire project at once.
              </>
            ),
            bullets: [
              'Per-clip TTS — Write dialog on individual clips for scene-specific narration',
              'Project-level TTS — Generate a single narration track that spans all clips',
              'Automatic voice assignment — When using characters with assigned voices, dialog uses the correct voice automatically',
              'Native audio upload — Skip TTS entirely by uploading your own recorded audio',
            ],
          },
          {
            id: 'tone-modifiers',
            heading: 'Tone Modifiers',
            content: (
              <>
                When writing dialog for characters, you can add tone modifiers that affect how the TTS engine delivers the line. These influence the emotional quality of the speech.
              </>
            ),
            bullets: [
              'calm — Relaxed, measured delivery',
              'excited — High energy, enthusiastic',
              'whispered — Soft, intimate, quiet',
              'angry — Intense, forceful',
              'urgent — Fast-paced, pressing',
              'playful — Light, fun, slightly teasing',
            ],
          },
        ],
      },
      {
        slug: 'avatars',
        title: 'Avatars',
        description: 'Use the avatar library for character representation and AI-powered avatar video generation with lip sync.',
        icon: ScanFace,
        sections: [
          {
            id: 'avatar-library',
            heading: 'Avatar Library',
            content: (
              <>
                Footage provides a library of pre-generated avatars — photorealistic character portraits that can be used as character reference images. These avatars are optimized for AI video generation and produce consistent results across clips.
              </>
            ),
          },
          {
            id: 'avatar-video-generation',
            heading: 'Avatar Video Generation',
            content: (
              <>
                Avatars support a specialized video generation pipeline. When you combine an avatar with dialog audio, Footage generates a talking-head video where the avatar appears to speak the dialog naturally. The dedicated avatar model produces realistic head movement and facial expressions.
              </>
            ),
          },
          {
            id: 'avatar-chunking',
            heading: 'Long-Form Avatar Content',
            content: (
              <>
                For longer dialog or narration, Footage automatically chunks the audio into segments and generates separate avatar video clips for each chunk. These are then stitched together seamlessly. This enables arbitrarily long avatar presentations without hitting per-clip duration limits.
              </>
            ),
            tip: 'Avatar videos work best with clear, well-paced dialog. Avoid extremely fast speech, as the lip-sync quality depends on having enough frames per phoneme.',
          },
        ],
      },
    ],
  },
  {
    label: 'Timeline & Editing',
    articles: [
      {
        slug: 'timeline',
        title: 'Timeline Overview',
        description: 'The timeline editor — your visual workspace for arranging clips, managing scenes, and building video sequences.',
        icon: Film,
        sections: [
          {
            id: 'timeline-basics',
            heading: 'Timeline Basics',
            content: (
              <>
                The timeline sits below the video preview in the project editor. Each clip appears as a card showing its prompt text, thumbnail (once generated), and current status. The timeline represents the sequential order of your video — clips play from left to right.
              </>
            ),
            image: {
              src: '/docs/timeline-clips.png',
              alt: 'Timeline clip cards showing thumbnails, prompts, character indicators, and generation status',
              caption: 'Clip cards on the timeline with AI-generated thumbnails, scene descriptions, and status indicators',
            },
          },
          {
            id: 'clip-cards',
            heading: 'Clip Cards',
            content: (
              <>
                Each clip card shows key information at a glance.
              </>
            ),
            bullets: [
              'Thumbnail — A preview frame from the generated video (or a storyboard image before generation)',
              'Prompt — The text description used for video generation',
              'Status — Pending, generating, completed, or failed',
              'Duration — The length of the clip in seconds',
              'Model — Which AI model was used (or will be used) for generation',
              'Dialog — Any voiceover text assigned to this clip',
            ],
          },
          {
            id: 'clip-operations',
            heading: 'Working with Clips',
            content: (
              <>
                The timeline supports standard editing operations.
              </>
            ),
            bullets: [
              'Reorder — Drag clips to rearrange their sequence',
              'Select — Click a clip to select it and view its details',
              'Edit prompt — Modify the text prompt and regenerate',
              'Duplicate — Create a copy of any clip for variations',
              'Delete — Remove clips from the timeline',
              'Generate — Start video generation for individual clips or all at once',
              'Regenerate — Generate a new version of an existing clip',
            ],
          },
          {
            id: 'zoom-and-navigation',
            heading: 'Zoom and Navigation',
            content: (
              <>
                Use the timeline zoom controls to adjust the view. Zoom in to see individual clip details, or zoom out for a high-level overview of your entire project. Keyboard shortcuts support quick navigation between clips.
              </>
            ),
          },
        ],
      },
      {
        slug: 'transitions-motion',
        title: 'Transitions & Motion',
        description: 'Add transitions between clips and apply motion presets for dynamic camera movement within clips.',
        icon: Layers,
        sections: [
          {
            id: 'transitions',
            heading: 'Clip Transitions',
            content: (
              <>
                Transitions control how one clip flows into the next. Select a transition type from the transition picker that appears between clips on the timeline. Options include cuts, dissolves, fades, and more.
              </>
            ),
          },
          {
            id: 'motion-presets',
            heading: 'Motion Presets',
            content: (
              <>
                Motion presets add camera movement to generated clips. These are applied as post-processing transforms — they don't re-generate the video, they animate the viewport.
              </>
            ),
            bullets: [
              'slow_zoom_in — Gradual push toward the subject',
              'zoom_in — Standard zoom toward subject',
              'dramatic_zoom_in — Fast, impactful zoom',
              'slow_zoom_out — Gradual reveal of the wider scene',
              'zoom_out — Standard zoom out',
              'pan_left / pan_right — Horizontal camera movement',
              'pan_up / pan_down — Vertical camera movement',
              'ken_burns_classic — Slow zoom + slight pan (documentary style)',
              'dramatic_push — Fast push toward subject with emphasis',
              'reveal_pan — Pan that reveals the subject or environment',
              'float — Subtle floating motion for ambient feel',
            ],
          },
          {
            id: 'keyframes',
            heading: 'Keyframe Animation',
            content: (
              <>
                For more control, use keyframes to animate any property over time. Set keyframe points on the timeline, adjust values at each point, and the property interpolates between them. Supported properties include position, scale, brightness, saturation, and any visual effect parameter.
              </>
            ),
            tip: 'The keyframe curve editor lets you choose interpolation types — linear, ease-in, ease-out — for natural-feeling motion.',
          },
        ],
      },
      {
        slug: 'text-overlays',
        title: 'Text Overlays',
        description: 'Add animated text elements to your clips — titles, lower thirds, captions, and more — with 28+ typography presets.',
        icon: Type,
        sections: [
          {
            id: 'adding-text',
            heading: 'Adding Text Overlays',
            content: (
              <>
                Text overlays let you place animated text on top of any clip. Use them for titles, lower thirds, call-to-action text, quotes, or any on-screen copy. Each text element has customizable content, style, position, and animation.
              </>
            ),
            image: {
              src: '/docs/text-overlays.png',
              alt: 'Text overlay presets panel showing Hero Title, Cinematic, Elegant Title, Neon, and Lower Thirds options',
              caption: 'Text preset library with titles, lower thirds, labels, and badge styles',
            },
          },
          {
            id: 'text-presets',
            heading: 'Text Presets',
            content: (
              <>
                Choose from 28+ pre-designed text presets organized into categories.
              </>
            ),
            bullets: [
              'Titles — cinematic_title, elegant_title, standard_title, chapter_title, hero_title, neon_title, youtube_bold, film_noir, vintage_poster, action_title',
              'Lower thirds — clean_lower_third, banner_lower_third, accent_lower_third, minimal_lower_third, gradient_lower_third',
              'Labels — top_badge, corner_label, bottom_tag, left_callout',
              'Quotes — center_quote, right_pullout',
              'News — breaking, statement, simple',
              'Specialty — typewriter_text, light_sans',
            ],
          },
          {
            id: 'text-customization',
            heading: 'Customization',
            content: (
              <>
                Every text preset is fully customizable. Adjust font family, size, color, background, position, and animation. Available fonts include Bebas Neue, Oswald, Playfair Display, Inter, Crimson Text, DM Serif Display, JetBrains Mono, and Bangers.
              </>
            ),
            bullets: [
              'Font family — Choose from 10+ fonts',
              'Position — center, lower_third, bottom_center, upper_third, and more',
              'Animation — zoom, fade, slide, typewriter, pop, blur, and other entrance effects',
              'Duration — Control how long the text is visible',
              'Subtitle — Optional secondary text line below the main text',
            ],
          },
        ],
      },
    ],
  },
  {
    label: 'Video Generation',
    articles: [
      {
        slug: 'ai-models',
        title: 'AI Models',
        description: 'Detailed breakdown of every AI video generation model available in Footage — capabilities, strengths, and when to use each one.',
        icon: Sparkles,
        sections: [
          {
            id: 'model-overview',
            heading: 'Model Overview',
            content: (
              <>
                Footage integrates multiple AI video generation models, each optimized for different use cases. You can set a default model per project or choose a specific model for individual clips. Models are continuously updated as new generations become available.
              </>
            ),
          },
          {
            id: 'generation-types',
            heading: 'Generation Types',
            content: (
              <>
                Each model supports one or more generation types. Understanding these helps you pick the right approach for each clip.
              </>
            ),
            bullets: [
              'Text-to-video — Generate a video from a text prompt alone. Best for creating new scenes from scratch.',
              'Image-to-video — Provide a starting image and animate it. Great for bringing stills to life.',
              'Reference-to-video — Use character reference images so the model maintains visual consistency across clips.',
              'Video-to-video — Edit or restyle an existing video clip. Change the mood, lighting, or style without starting over.',
              'First+Last frame — Specify both starting and ending frames for precise motion control.',
              'Video extension — Extend an existing clip by several seconds to continue the action.',
            ],
          },
          {
            id: 'model-capabilities',
            heading: 'Model Capabilities',
            content: (
              <>
                Footage offers several tiers of video models with different strengths.
              </>
            ),
            bullets: [
              'Standard models — Fast generation (30-60s), great for iteration and prototyping. Supports 3-15 second clips.',
              'Pro models — Higher visual quality with better motion coherence and physics. Support character references and multi-prompt scenes.',
              'Fast models — Optimized for speed with slightly shorter maximum durations. Ideal for quick previews and rapid iteration.',
            ],
            note: 'All models support aspect ratios of 16:9, 9:16, and 1:1. Footage automatically selects the best model for your project settings, but you can override the choice per clip.',
          },
          {
            id: 'additional-features',
            heading: 'Additional Model Features',
            content: (
              <>
                Depending on the model, you may have access to these additional capabilities.
              </>
            ),
            bullets: [
              'Built-in audio — Some models generate ambient sound alongside the video',
              'Voice IDs — Assign character voices directly in the generation prompt (up to 2 per clip)',
              'Negative prompts — Specify what you don\'t want in the scene to avoid unwanted elements',
              'CFG scale — Fine-tune how closely the model follows your prompt vs. creative freedom',
              'Multi-prompt — Choreograph complex scenes with multiple shot descriptions in a single clip',
            ],
            tip: 'When character consistency matters most, use a model that supports reference-to-video generation. It directly uses your character\'s reference images to match faces and appearances across clips.',
          },
          {
            id: 'choosing-a-model',
            heading: 'Choosing the Right Model',
            content: (
              <>
                Use these guidelines to pick the best model for your needs.
              </>
            ),
            bullets: [
              'General purpose — Start with the default pro model for the best balance of quality and speed',
              'Character consistency — Use a reference-to-video model when the same character appears across multiple clips',
              'Highest visual quality — Pro models with longer generation times produce the most polished output',
              'Rapid prototyping — Fast models let you iterate quickly on prompts before committing to a high-quality render',
              'Video editing — Video-to-video models let you restyle or modify existing clips without starting from scratch',
              'Longer scenes — Some models support extended durations up to 15 seconds per clip',
            ],
          },
        ],
      },
      {
        slug: 'generation-pipeline',
        title: 'Generation Pipeline',
        description: 'How video generation works under the hood — from prompt submission to final rendered clip.',
        icon: Sparkles,
        sections: [
          {
            id: 'pipeline-overview',
            heading: 'Pipeline Overview',
            content: (
              <>
                When you generate a clip, Footage orchestrates a multi-step pipeline. Understanding this pipeline helps you troubleshoot issues and optimize your workflow.
              </>
            ),
            image: {
              src: '/docs/project-editor.png',
              alt: 'Project editor showing clips in various generation states on the timeline',
              caption: 'Clips move through generation stages visible on the timeline — from pending to completed',
            },
          },
          {
            id: 'generation-steps',
            heading: 'Generation Steps',
            content: (
              <>
                Each clip goes through these stages.
              </>
            ),
            steps: [
              { title: 'Pending', content: 'Clip is queued for generation.' },
              { title: 'Generating Image', content: 'A storyboard preview image is created first — fast, usually under 10 seconds.' },
              { title: 'Image Ready', content: 'Storyboard image is available for preview before committing to full video generation.' },
              { title: 'Generating Video', content: 'The AI model is producing the video clip. Takes 30-120 seconds depending on model and duration.' },
              { title: 'Completed', content: 'Video is generated, uploaded to CDN, and ready to play.' },
            ],
            note: 'If a clip fails (content policy violation, timeout, or model error), you can edit the prompt and retry. Failed clips don\'t consume additional credits.',
          },
          {
            id: 'webhooks',
            heading: 'Webhook-Based Architecture',
            content: (
              <>
                Video generation is asynchronous. When you start generation, Footage sends the request to the AI model and processes it in the background. When the video is ready, it's automatically uploaded to Footage's CDN and the clip status updates in real time.
              </>
            ),
            tip: 'You can close the browser or navigate away while clips are generating. The process runs entirely in the cloud. When you return, your clips will be ready.',
          },
          {
            id: 'aspect-ratios',
            heading: 'Aspect Ratios',
            content: (
              <>
                Set the aspect ratio per-project to match your target platform.
              </>
            ),
            bullets: [
              '16:9 — Landscape (YouTube, presentations, web video)',
              '9:16 — Portrait (TikTok, Instagram Reels, YouTube Shorts)',
              '1:1 — Square (Instagram feed, social media posts)',
            ],
          },
          {
            id: 'generation-tips',
            heading: 'Tips for Better Results',
            content: (
              <>
                A few things to keep in mind for optimal generation quality.
              </>
            ),
            bullets: [
              'Write detailed prompts — The model can only generate what it\'s told. Include camera angle, lighting, motion, and environment details.',
              'Use reference images when possible — They dramatically improve character consistency.',
              'Match the model to the content — Different models excel at different styles and subjects.',
              'Use the storyboard step — Review the preview image before committing to full video generation.',
              'Iterate on prompts, not on luck — If a generation doesn\'t match your vision, adjust the prompt rather than just regenerating with the same text.',
            ],
          },
        ],
      },
    ],
  },
  {
    label: 'Audio & Music',
    articles: [
      {
        slug: 'music-library',
        title: 'Music Library',
        description: 'Browse 282 royalty-free tracks across 14 categories. Search, preview, and add background music to your projects.',
        icon: Music,
        sections: [
          {
            id: 'library-overview',
            heading: 'Library Overview',
            content: (
              <>
                Footage includes a built-in library of 282 royalty-free music tracks. All tracks are cleared for commercial use — no licensing fees, no attribution required. Use them as background music in any project.
              </>
            ),
            image: {
              src: '/docs/music-library.png',
              alt: 'Music library panel showing 282 tracks with search, category filters, and preview buttons',
              caption: 'Browse 282 royalty-free tracks with search, category filters, and instant preview',
            },
          },
          {
            id: 'categories',
            heading: 'Music Categories',
            content: (
              <>
                Tracks are organized into 14 categories to help you find the right mood.
              </>
            ),
            bullets: [
              'Chill — Relaxed, ambient, lo-fi',
              'Classical — Orchestral, piano, chamber music',
              'Comedy — Lighthearted, playful, quirky',
              'Country — Acoustic, folk, Americana',
              'Electronic — Synth, EDM, techno, chillwave',
              'Epic / Dramatic — Cinematic, trailer music, orchestral swells',
              'Fantasy — Ethereal, magical, adventure',
              'Horror — Dark, tense, atmospheric',
              'Jazz — Smooth, big band, bebop',
              'Rock — Guitar-driven, indie, alternative',
              'Romantic / Sentimental — Emotional, heartfelt, tender',
              'Underscoring — Subtle background music for narration',
              'Uplifting — Positive, motivational, energetic',
              'World — Cultural, ethnic, global sounds',
            ],
          },
          {
            id: 'search-and-preview',
            heading: 'Search and Preview',
            content: (
              <>
                Use the search bar in the music picker to find tracks by name or keyword. Click any track to preview it before adding it to your project. The music picker shows track name, category, and duration.
              </>
            ),
          },
          {
            id: 'adding-music',
            heading: 'Adding Music to Your Project',
            content: (
              <>
                Select a track from the library to add it to your project's audio mix. Music is added as an audio track on the timeline, where you can adjust its start time, duration, volume, and fade in/out.
              </>
            ),
          },
        ],
      },
      {
        slug: 'audio-mixing',
        title: 'Audio Mixing',
        description: 'Layer and mix audio tracks — voiceovers, music, and sound effects — with per-clip volume, trim, and fade controls.',
        icon: Mic,
        sections: [
          {
            id: 'audio-tracks',
            heading: 'Audio Tracks',
            content: (
              <>
                Each clip can have multiple audio tracks layered together. Audio track types include music, sound effects (SFX), and voiceover. Each track has independent controls for volume, timing, and fades.
              </>
            ),
          },
          {
            id: 'track-properties',
            heading: 'Track Properties',
            content: (
              <>
                Every audio track has these adjustable properties.
              </>
            ),
            bullets: [
              'Start time — When the audio begins playing (seconds from video start)',
              'Duration — How long the audio plays',
              'Trim start — Left-edge trim offset (skip the beginning of the audio file)',
              'Volume — Level from 0 (silent) to 1 (full)',
              'Fade in — Gradual volume increase at the start (seconds)',
              'Fade out — Gradual volume decrease at the end (seconds)',
            ],
          },
          {
            id: 'audio-mix',
            heading: 'Project Audio Mix',
            content: (
              <>
                The project-level audio mix controls mute states and volumes across all clips. Use it to balance voiceover levels against background music, or to temporarily mute specific tracks while editing.
              </>
            ),
          },
        ],
      },
      {
        slug: 'voiceovers',
        title: 'Voiceovers',
        description: 'Generate AI voiceovers for your clips using text-to-speech with character-specific voices and tone modifiers.',
        icon: Mic,
        sections: [
          {
            id: 'per-clip-voiceover',
            heading: 'Per-Clip Voiceover',
            content: (
              <>
                Write dialog text on any clip to generate a voiceover for that specific scene. The text is converted to speech using AI TTS. Each clip can use a different voice, or you can assign voices to characters and let the system match automatically.
              </>
            ),
          },
          {
            id: 'project-narration',
            heading: 'Project-Level Narration',
            content: (
              <>
                For a consistent narrator voice across your entire video, use project-level TTS. Write a single narration script and Footage generates the audio, then distributes it across clips with proper timing.
              </>
            ),
          },
          {
            id: 'tts-workflow',
            heading: 'TTS Workflow',
            content: (
              <>
                The TTS generation process.
              </>
            ),
            steps: [
              { title: 'Write dialog', content: 'Add dialog text on clips or have Footage write it for you as part of the narrative.' },
              { title: 'Assign a voice', content: 'Pick from the voice library or use the character\'s assigned voice. Each clip can use a different voice.' },
              { title: 'Generate voiceover', content: <>Click <strong>"Generate Voiceover"</strong> — audio is generated in seconds.</> },
              { title: 'Preview and adjust', content: 'Listen to the voiceover on the clip. Adjust dialog text and regenerate if needed.' },
            ],
          },
          {
            id: 'native-audio',
            heading: 'Custom Audio Upload',
            content: (
              <>
                If you prefer to record your own voiceover, you can upload audio files directly. This skips the TTS step entirely. Uploaded audio is treated the same as generated voiceover — it appears as an audio track on the clip.
              </>
            ),
          },
        ],
      },
      {
        slug: 'lip-sync',
        title: 'Lip Sync',
        description: 'AI post-processing that matches character mouth movements to voiceover audio for realistic talking-head videos.',
        icon: ScanFace,
        sections: [
          {
            id: 'how-lip-sync-works',
            heading: 'How Lip Sync Works',
            content: (
              <>
                Lip sync is an AI-powered post-processing step. After a clip is generated and has voiceover audio, the lip sync process analyzes the audio, identifies phonemes, and modifies the character's mouth movements in the video to match the speech. The result is a natural-looking talking-head video.
              </>
            ),
          },
          {
            id: 'lip-sync-pipeline',
            heading: 'Lip Sync Pipeline',
            content: (
              <>
                The lip sync process follows these stages.
              </>
            ),
            steps: [
              { title: 'Prerequisites', content: 'The clip must have both a generated video and a voiceover audio track.' },
              { title: 'Pending', content: 'Lip sync is queued for processing.' },
              { title: 'Processing', content: 'AI analyzes the audio waveform, identifies phonemes, and modifies character mouth movements in the video.' },
              { title: 'Mixing', content: 'The modified video and original audio are combined into the final output.' },
              { title: 'Ready', content: 'Lip-synced video is complete and replaces the original clip.' },
            ],
          },
          {
            id: 'lip-sync-tips',
            heading: 'Tips for Best Results',
            content: (
              <>
                Lip sync quality depends on the input video and audio.
              </>
            ),
            bullets: [
              'Use clear frontal face shots — Profile views or distant subjects produce less accurate results',
              'Ensure clear audio — Clean voiceover without background noise works best',
              'Keep dialog pacing natural — Extremely fast speech can reduce lip sync quality',
              'The character should be visible and relatively still — Excessive movement can interfere with tracking',
            ],
            tip: 'Lip sync processes entirely in the cloud. You can continue working on other clips while it runs.',
          },
        ],
      },
      {
        slug: 'captions',
        title: 'Captions',
        description: 'Auto-transcription with word-level timing, customizable caption styles, and word-by-word animations.',
        icon: Captions,
        sections: [
          {
            id: 'auto-transcription',
            heading: 'Automatic Transcription',
            content: (
              <>
                Footage automatically transcribes voiceover audio into word-by-word captions. Each word gets precise timing data, enabling word-level highlighting and one-word-at-a-time animation. Transcription runs in the cloud and typically completes in a few seconds.
              </>
            ),
          },
          {
            id: 'caption-styles',
            heading: 'Caption Style Presets',
            content: (
              <>
                Choose from multiple caption style categories, each with several preset options.
              </>
            ),
            bullets: [
              'Cinematic — warm, cool, bleach bypass, noir, film fade',
              'Mood — golden hour, moonlight, teal & orange',
              'Stylized — vintage, sepia, black & white, cross process',
              'Technical — desaturated, high contrast',
            ],
          },
          {
            id: 'caption-customization',
            heading: 'Customization Options',
            content: (
              <>
                Every caption style is customizable. Adjust font, size, color, background, position, and animation type. Caption animations include fill (word-by-word highlight), outline, fade, and slide effects. Position captions at the bottom, center, or top of the frame.
              </>
            ),
          },
          {
            id: 'editing-captions',
            heading: 'Editing Transcribed Text',
            content: (
              <>
                If the transcription gets a word wrong (common with proper nouns, technical terms, or accented speech), you can edit the text directly. Changes update the visual captions in real time. Timing data is preserved when you edit text.
              </>
            ),
            tip: 'Captions improve your video\'s accessibility and SEO. When shared publicly, search engines can index the caption text, making your video discoverable.',
          },
        ],
      },
    ],
  },
  {
    label: 'Visual Effects',
    articles: [
      {
        slug: 'color-grading',
        title: 'Color Grading',
        description: '13 professional color grade presets — from cinematic warm tones to vintage film looks — with intensity control.',
        icon: Palette,
        sections: [
          {
            id: 'grades-overview',
            heading: 'Color Grade Presets',
            content: (
              <>
                Apply professional color grading to any clip. Each grade transforms the color palette, contrast, and tone of the video to achieve a specific cinematic look. Adjust the intensity slider to control how strongly the grade is applied (0-100%).
              </>
            ),
            image: {
              src: '/docs/effects-panel.png',
              alt: 'Effects panel showing color grade presets organized by category: Cinematic, Mood, Stylized, and Technical',
              caption: 'The Effects panel with color grades organized into Cinematic, Mood, Stylized, and Technical categories',
            },
          },
          {
            id: 'cinematic-grades',
            heading: 'Cinematic Grades',
            content: (
              <>
                Classic film looks used in professional cinema.
              </>
            ),
            bullets: [
              'Warm — Golden, inviting tones (sunset feel)',
              'Cool — Blue-shifted, clean, modern look',
              'Bleach Bypass — Desaturated with high contrast (war film aesthetic)',
              'Noir — High contrast black and white with deep blacks',
              'Film Fade — Lifted blacks, faded look (indie film style)',
            ],
          },
          {
            id: 'mood-grades',
            heading: 'Mood Grades',
            content: (
              <>
                Grades that evoke specific atmospheric moods.
              </>
            ),
            bullets: [
              'Golden Hour — Warm, soft, diffused sunlight',
              'Moonlight — Cool blue-silver tones (night scenes)',
              'Teal & Orange — Hollywood blockbuster color palette',
            ],
          },
          {
            id: 'stylized-grades',
            heading: 'Stylized Grades',
            content: (
              <>
                Creative looks for artistic effect.
              </>
            ),
            bullets: [
              'Vintage — Aged film stock with slight color shift',
              'Sepia — Classic warm monochrome',
              'B&W — Pure black and white',
              'Cross Process — Shifted colors with increased saturation (lomography effect)',
            ],
          },
          {
            id: 'technical-grades',
            heading: 'Technical Grades',
            content: (
              <>
                Functional adjustments for specific use cases.
              </>
            ),
            bullets: [
              'Desaturated — Reduced color intensity for a muted, editorial look',
              'High Contrast — Increased dynamic range for punch and impact',
            ],
          },
        ],
      },
      {
        slug: 'effects',
        title: 'Effects & Adjustments',
        description: 'Brightness, contrast, blur, VHS, bloom, shake, and more — all animatable with keyframes.',
        icon: Wand2,
        sections: [
          {
            id: 'basic-adjustments',
            heading: 'Basic Adjustments',
            content: (
              <>
                Standard image adjustments for fine-tuning the look of any clip.
              </>
            ),
            bullets: [
              'Brightness — Lighten or darken the overall image',
              'Contrast — Increase or decrease the difference between light and dark areas',
              'Saturation — Boost or reduce color intensity',
              'Hue Rotation — Shift the entire color spectrum',
            ],
          },
          {
            id: 'cinematic-effects',
            heading: 'Cinematic Effects',
            content: (
              <>
                Effects that add cinematic character to your clips.
              </>
            ),
            bullets: [
              'Letterbox — Add black bars for a widescreen cinematic look (customizable aspect ratio)',
              'Vignette — Darken the edges of the frame to draw focus to the center',
              'Bloom — Add a soft glow/light bloom around bright areas',
            ],
          },
          {
            id: 'creative-effects',
            heading: 'Creative Effects',
            content: (
              <>
                More dramatic effects for stylized content.
              </>
            ),
            bullets: [
              'Blur — Gaussian or motion blur for depth of field or motion effects',
              'Chromatic Aberration — RGB channel separation for a glitchy, optical distortion look',
              'VHS — Retro video distortion with scan lines, color bleeding, and tracking artifacts',
              'Shake — Camera shake simulation for handheld or action feel',
            ],
          },
          {
            id: 'keyframing-effects',
            heading: 'Animating Effects with Keyframes',
            content: (
              <>
                Every effect parameter can be animated over time using keyframes. Place keyframe points at different moments in the clip and set different values at each point. The effect smoothly transitions between keyframe values. Use the curve editor to control interpolation (linear, ease-in, ease-out).
              </>
            ),
            tip: 'Use keyframed brightness to create a fade-in/fade-out effect, or keyframe the VHS effect intensity to transition from clean footage to a distorted look.',
            cards: [
              { icon: Palette, title: 'Color Grading', description: '13 professional color grade presets', href: '/docs/color-grading' },
              { icon: Layers, title: 'Transitions & Motion', description: 'Camera movement and clip transitions', href: '/docs/transitions-motion' },
            ],
          },
        ],
      },
    ],
  },
  {
    label: 'Sharing & Export',
    articles: [
      {
        slug: 'sharing',
        title: 'Share Links',
        description: 'Generate public share links for your videos — anyone can view them without signing in.',
        icon: Share2,
        sections: [
          {
            id: 'public-share-links',
            heading: 'Public Share Links',
            content: (
              <>
                Every project can generate a public share link. Anyone with the link can view your video without signing in or creating an account. The share page displays the video player, project title, and description in a clean, branded layout.
              </>
            ),
          },
          {
            id: 'share-page',
            heading: 'Share Page Features',
            content: (
              <>
                The share page includes several features for viewers.
              </>
            ),
            bullets: [
              'Video player with full playback controls',
              'Project title and description',
              'Download button — viewers can download the video file',
              'Copy link — share the URL easily',
              'Light/dark theme toggle',
              'Responsive design — works on desktop and mobile',
            ],
          },
          {
            id: 'shortcodes',
            heading: 'Shortcodes',
            content: (
              <>
                Each film gets a unique 6-character shortcode. Share links use this shortcode: <strong>{SITE_HOST}/share/your-code</strong>. Shortcodes are permanent — even if you rename the film, the share link stays the same.
              </>
            ),
            cards: [
              { icon: MonitorPlay, title: 'Embedding', description: 'Embed videos on any website', href: '/docs/embedding' },
              { icon: Share2, title: 'Exporting', description: 'Download and export your film', href: '/docs/exporting' },
            ],
          },
        ],
      },
      {
        slug: 'embedding',
        title: 'Embedding',
        description: 'Embed your Footage videos on any website with a lightweight player. Supports autoplay, looping, and responsive sizing.',
        icon: MonitorPlay,
        sections: [
          {
            id: 'embed-player',
            heading: 'Embed Player',
            content: (
              <>
                The embed player is a lightweight, bare video player designed for third-party websites. It loads fast, has minimal UI, and supports standard embed parameters.
              </>
            ),
          },
          {
            id: 'embed-parameters',
            heading: 'Embed Parameters',
            content: (
              <>
                Customize the embed behavior with URL query parameters.
              </>
            ),
            bullets: [
              'autoplay=1 — Start playing automatically when the page loads',
              'muted=1 — Start with audio muted (required for autoplay in most browsers)',
              'loop=1 — Loop the video continuously',
              'controls=0 — Hide playback controls (controls shown by default)',
            ],
            note: 'Most browsers require muted=1 for autoplay to work. Set both autoplay=1 and muted=1 for reliable auto-playing embeds.',
          },
          {
            id: 'embed-code',
            heading: 'Using the Embed Code',
            content: (
              <>
                Copy the embed code from your project's share settings. The embed uses a standard iframe that's responsive and works on any website. Example: <code>&lt;iframe src="{SITE_URL}/embed/your-id?autoplay=1&muted=1" width="100%" style="aspect-ratio:16/9" frameborder="0" allowfullscreen&gt;&lt;/iframe&gt;</code>
              </>
            ),
          },
        ],
      },
      {
        slug: 'exporting',
        title: 'Exporting',
        description: 'Export your finished project as a single video file with all clips, voiceovers, music, effects, and captions rendered together.',
        icon: Share2,
        sections: [
          {
            id: 'export-process',
            heading: 'Export Process',
            content: (
              <>
                When you export a project, Footage renders all clips together into a single video file. The render process stitches clips in timeline order, applies voiceovers and music, composites text overlays and captions, applies color grading and effects, and encodes the final output.
              </>
            ),
          },
          {
            id: 'cloud-rendering',
            heading: 'Cloud Rendering',
            content: (
              <>
                Exports are rendered in the cloud using a dedicated Remotion render service. This means you don't need a powerful computer — the rendering happens on Footage's servers. You can close the browser and come back when it's done.
              </>
            ),
            tip: 'Export rendering time depends on project length, number of clips, and complexity of effects. Most projects export in under a minute.',
          },
          {
            id: 'download',
            heading: 'Download',
            content: (
              <>
                Once the export is complete, download the video file directly. The rendered video is also stored on Footage's CDN, so you can download it again later from your project or share page.
              </>
            ),
          },
        ],
      },
    ],
  },
  {
    label: 'Account & Billing',
    articles: [
      {
        slug: 'plans-and-pricing',
        title: 'Plans & Pricing',
        description: 'Free, Pro, Max, and Ultra plans — what each includes and how to choose the right one.',
        icon: CreditCard,
        sections: [
          {
            id: 'available-plans',
            heading: 'Available Plans',
            content: (
              <>
                Footage offers four plans to match different usage levels.
              </>
            ),
            bullets: [
              'Free ($0) — Get started with basic features. Create projects with AI, manage content, basic support.',
              'Pro ($29/month) — For creators who need more. Standard usage limits, all AI models, priority support.',
              'Max ($99/month) — For production workflows. High-volume usage limits, all AI models, priority support.',
              'Ultra ($249/month) — Maximum power for teams. Highest usage limits, all AI models, priority support.',
            ],
          },
          {
            id: 'credits-system',
            heading: 'How Credits Work',
            content: (
              <>
                Each paid plan includes a monthly credit allowance. Credits are consumed when you generate video clips, voiceovers, lip sync, and other AI operations. Different operations cost different amounts of credits. Your credit balance resets at the start of each billing cycle.
              </>
            ),
            note: 'Credit usage varies by model and operation. Higher-quality models and longer durations use more credits. Your remaining balance is always visible in the sidebar.',
          },
          {
            id: 'upgrading',
            heading: 'Upgrading Your Plan',
            content: (
              <>
                Upgrade from <strong>Settings</strong> in the sidebar, or click the upgrade prompt that appears when you're running low on credits. Plan changes take effect immediately — you get access to the new credit limit right away. Billing is prorated.
              </>
            ),
          },
        ],
      },
      {
        slug: 'account-settings',
        title: 'Account Settings',
        description: 'Manage your profile, subscription, and app preferences.',
        icon: Settings,
        sections: [
          {
            id: 'profile',
            heading: 'Profile',
            content: (
              <>
                View and edit your account profile — name, email, and avatar. Your profile information appears on shared videos and in community features.
              </>
            ),
          },
          {
            id: 'subscription',
            heading: 'Subscription Management',
            content: (
              <>
                View your current plan, credit balance, and usage for the current billing period. Upgrade, downgrade, or cancel your subscription. Subscription management is handled through Stripe's secure customer portal.
              </>
            ),
          },
          {
            id: 'cancellation',
            heading: 'Cancellation',
            content: (
              <>
                Cancel your subscription at any time from the Settings page. Your account reverts to the Free plan at the end of your current billing period. All your projects and content are retained — you can still access and view them, but generation features may be limited.
              </>
            ),
          },
        ],
      },
    ],
  },
  {
    label: 'CLI & API',
    articles: [
      {
        slug: 'cli',
        title: 'CLI Setup',
        description: 'Install and authenticate the Footage CLI for command-line video generation and automation.',
        icon: Terminal,
        sections: [
          {
            id: 'cli-overview',
            heading: 'CLI Overview',
            content: (
              <>
                The Footage CLI lets you generate videos from the command line. It's useful for automation, batch operations, and integrating Footage into your existing workflows or CI/CD pipelines.
              </>
            ),
          },
          {
            id: 'authentication',
            heading: 'Authentication',
            content: (
              <>
                Authenticate the CLI by creating a token in the web app. Go to <strong>/cli</strong> in the browser, click <strong>"Create Token"</strong>, and copy the token value. Set it as the <code>SANDBOX_API_KEY</code> environment variable, or pass it directly to CLI commands.
              </>
            ),
            tip: 'CLI tokens are hashed and stored securely. Each token is tied to your user account. You can revoke tokens at any time from the CLI page.',
          },
          {
            id: 'browser-login',
            heading: 'Browser-Based Login',
            content: (
              <>
                For a faster login flow, use the browser-based CLI auth. Run the CLI login command and it will open a browser window where you authenticate with your existing Footage account. The CLI automatically receives the token after successful login.
              </>
            ),
            cards: [
              { icon: Terminal, title: 'CLI Commands', description: 'Generate, iterate, and manage videos from the command line', href: '/docs/cli-commands' },
            ],
          },
        ],
      },
      {
        slug: 'cli-commands',
        title: 'CLI Commands',
        description: 'Generate videos, check status, iterate on results, and manage video groups from the command line.',
        icon: Terminal,
        sections: [
          {
            id: 'available-commands',
            heading: 'Available Commands',
            content: (
              <>
                The CLI supports several core commands for video generation and management.
              </>
            ),
          },
          {
            id: 'models',
            heading: 'List Models',
            content: (
              <>
                List all available AI video generation models and their capabilities. Use this to find the right model identifier for generation commands.
              </>
            ),
          },
          {
            id: 'generate',
            heading: 'Generate a Video',
            content: (
              <>
                Generate a video clip from a text prompt. Specify the model, prompt text, and duration. The <code>--wait</code> flag polls until the generation completes or fails, giving you the result inline.
              </>
            ),
          },
          {
            id: 'status',
            heading: 'Check Status',
            content: (
              <>
                Check the status of a video generation by its ID. Returns the current state (pending, generating, completed, failed), progress percentage, and the video URL when complete.
              </>
            ),
          },
          {
            id: 'iterate',
            heading: 'Iterate on a Video',
            content: (
              <>
                Generate a new version of a video within a group. Groups are collections of related video iterations. Use <code>iterate</code> to try different prompts, models, or settings while keeping all versions organized together.
              </>
            ),
          },
        ],
      },
    ],
  },
]
